/**
 * Deck Management Utilities for Texas Hold'em Poker
 *
 * Provides functions for creating, shuffling, and dealing from a standard
 * 52-card deck. All functions are pure — they return new Deck objects
 * rather than mutating the input.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 5.2, 6.2, 7.2
 */

import { Card, Deck, Suit, Rank, SUITS, RANKS } from './types';

/**
 * Create a standard 52-card deck (4 suits × 13 ranks)
 * with empty dealtCards and burnCards arrays.
 */
export function createDeck(): Deck {
  const cards: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      cards.push({ suit, rank });
    }
  }
  return {
    cards,
    dealtCards: [],
    burnCards: [],
  };
}

/**
 * Shuffle the deck's remaining cards using the Fisher-Yates algorithm.
 * Returns a new Deck with shuffled cards.
 */
export function shuffleDeck(deck: Deck): Deck {
  const cards = [...deck.cards];
  for (let i = cards.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cards[i], cards[j]] = [cards[j], cards[i]];
  }
  return {
    ...deck,
    cards,
  };
}

/**
 * Deal one card from the top of the deck.
 * The card is moved from `cards` to `dealtCards`.
 * Throws if the deck is empty.
 */
export function dealCard(deck: Deck): { card: Card; deck: Deck } {
  if (deck.cards.length === 0) {
    throw new Error('Cannot deal from an empty deck');
  }
  const [card, ...remaining] = deck.cards;
  return {
    card,
    deck: {
      ...deck,
      cards: remaining,
      dealtCards: [...deck.dealtCards, card],
    },
  };
}

/**
 * Burn the top card of the deck (remove it to burnCards).
 * Throws if the deck is empty.
 */
export function burnCard(deck: Deck): Deck {
  if (deck.cards.length === 0) {
    throw new Error('Cannot burn from an empty deck');
  }
  const [card, ...remaining] = deck.cards;
  return {
    ...deck,
    cards: remaining,
    burnCards: [...deck.burnCards, card],
  };
}

/**
 * Deal 2 hole cards to each player in clockwise order.
 * Cards are dealt one at a time per player, then a second round —
 * mimicking real poker dealing.
 *
 * @param deck - The current deck state
 * @param playerCount - Number of players (2–8)
 * @returns holeCards[playerIndex] = [card1, card2], plus updated deck
 */
export function dealHoleCards(
  deck: Deck,
  playerCount: number
): { holeCards: Card[][]; deck: Deck } {
  if (playerCount < 2 || playerCount > 8) {
    throw new Error(`Invalid player count: ${playerCount}. Must be 2–8.`);
  }
  if (deck.cards.length < playerCount * 2) {
    throw new Error(
      `Not enough cards to deal hole cards. Need ${playerCount * 2}, have ${deck.cards.length}.`
    );
  }

  const holeCards: Card[][] = Array.from({ length: playerCount }, () => []);
  let currentDeck = deck;

  // First round: one card to each player
  for (let i = 0; i < playerCount; i++) {
    const result = dealCard(currentDeck);
    holeCards[i].push(result.card);
    currentDeck = result.deck;
  }

  // Second round: one card to each player
  for (let i = 0; i < playerCount; i++) {
    const result = dealCard(currentDeck);
    holeCards[i].push(result.card);
    currentDeck = result.deck;
  }

  return { holeCards, deck: currentDeck };
}

/**
 * Deal community cards: burn one card, then deal `count` cards.
 *
 * @param deck - The current deck state
 * @param count - Number of community cards to deal (typically 3 for flop, 1 for turn/river)
 * @returns The dealt community cards and updated deck
 */
export function dealCommunityCards(
  deck: Deck,
  count: number
): { cards: Card[]; deck: Deck } {
  if (deck.cards.length < count + 1) {
    throw new Error(
      `Not enough cards. Need ${count + 1} (1 burn + ${count} deal), have ${deck.cards.length}.`
    );
  }

  // Burn one card first
  let currentDeck = burnCard(deck);

  // Deal the community cards
  const cards: Card[] = [];
  for (let i = 0; i < count; i++) {
    const result = dealCard(currentDeck);
    cards.push(result.card);
    currentDeck = result.deck;
  }

  return { cards, deck: currentDeck };
}

/**
 * Verify deck integrity: no duplicates, and the union of
 * cards + dealtCards + burnCards always equals 52.
 *
 * @returns true if the deck is valid, false otherwise
 */
export function verifyDeckIntegrity(deck: Deck): boolean {
  const allCards = [...deck.cards, ...deck.dealtCards, ...deck.burnCards];

  // Total must be 52
  if (allCards.length !== 52) {
    return false;
  }

  // No duplicates
  const seen = new Set<string>();
  for (const card of allCards) {
    const key = `${card.rank}-${card.suit}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
  }

  return true;
}
